import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../../utils/api';
import SteamFlag from '../common/SteamFlag';
import { fetchCountriesBulk } from '../../utils/countries';
import './Leagues.css';

/**
 * Prettify a canonical map tag for display (e.g. "de_mirage" → "Mirage").
 * Numeric workshop ids are shown unchanged.
 *
 * @param {string} tag - The canonical map_name tag.
 * @returns {string} A human-friendly map label.
 */
function prettyMap(tag) {
  if (!tag) return '–';
  if (/^\d+$/.test(tag)) return tag;
  const base = tag.replace(/^(de|cs|dz|ar|gd)_/, '');
  return base.charAt(0).toUpperCase() + base.slice(1);
}

/**
 * All-time league statistics: an MVP board aggregated across every season plus a
 * per-map breakdown (play counts + the top player on each map). Self-hides until
 * the league has at least one coupled match with demo-derived stats, so it can be
 * dropped into the league overview unconditionally.
 *
 * @param {object} props - Component props.
 * @param {string} props.leagueId - The league id to fetch all-time stats for.
 * @returns {JSX.Element|null} The stats sections, or null when there's no data.
 */
export default function LeagueAllTimeStats({ leagueId }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${API_URL}/api/leagues/${leagueId}/stats`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (d) {
          const ids = (d.mvp || []).map((p) => p.steam_id).filter(Boolean);
          (d.maps || []).forEach((m) =>
            (m.top_players || []).forEach((p) => p.steam_id && ids.push(p.steam_id)));
          if (ids.length) fetchCountriesBulk(ids);
        }
      })
      .catch(() => alive && setData(null));
    return () => {
      alive = false;
    };
  }, [leagueId]);

  if (!data || ((data.mvp || []).length === 0 && (data.maps || []).length === 0)) {
    return null;
  }

  const openPlayer = (steamId) => {
    if (steamId) navigate(`/stats/players/${steamId}`);
  };

  return (
    <div className="lstat-wrap">
      <h2 className="league-section-title">📊 {t('leagues.allTimeStats')}</h2>

      {data.mvp.length > 0 && (
        <div className="team-card notched-card lstat-card">
          <h3 className="division-title">{t('leagues.mvpBoard')}</h3>
          <table className="lstat-table">
            <thead>
              <tr>
                <th className="lstat-th-rank">#</th>
                <th>{t('nav.players')}</th>
                <th title={t('nav.matches')}>M</th>
                <th title="K/D">K/D</th>
                <th title={t('leagues.adrTitle')}>ADR</th>
                <th title={t('leagues.hsTitle')}>HS%</th>
                <th title="Rating">Rating</th>
              </tr>
            </thead>
            <tbody>
              {data.mvp.slice(0, 25).map((p, i) => (
                <tr key={p.steam_id}>
                  <td className="lstat-td-rank">{i + 1}</td>
                  <td>
                    <button className="lstat-player" onClick={() => openPlayer(p.steam_id)}>
                      <SteamFlag steamid64={p.steam_id} className="lstat-flag" />
                      <span className="lstat-player-name">{p.name}</span>
                    </button>
                  </td>
                  <td className="lstat-num">{p.matches}</td>
                  <td className="lstat-num">{p.kd}</td>
                  <td className="lstat-num">{p.adr}</td>
                  <td className="lstat-num">{p.hs_pct}</td>
                  <td className="lstat-num lstat-rating">{p.rating}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.maps.length > 0 && (
        <div className="team-card notched-card lstat-card">
          <h3 className="division-title">{t('nav.maps')}</h3>
          <div className="lstat-map-grid">
            {data.maps.map((m) => {
              const best = m.top_players?.[0];
              return (
                <div key={m.map_name} className="lstat-map">
                  <div className="lstat-map-head">
                    <span className="lstat-map-name">{prettyMap(m.map_name)}</span>
                    <span className="lstat-map-count">{t('leagues.mapsPlayed', { count: m.maps_played })}</span>
                  </div>
                  <div className="lstat-map-body">
                    {best && <span className="lstat-map-best-label">{t('leagues.best')}</span>}
                    {best && (
                      <button
                        className="lstat-map-best"
                        onClick={() => openPlayer(best.steam_id)}
                        title={t('leagues.bestRatingTitle')}
                      >
                        <SteamFlag steamid64={best.steam_id} className="lstat-flag" />
                        <span className="lstat-player-name">{best.name}</span>
                        <span className="lstat-map-best-rating">{best.rating}</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
